"use client";

import type { OnboardingReferenceData } from "@clutch/auth/reference-data";
import { Cancel01Icon, Location01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ProfileGapDialog } from "@/components/profile-gap/profile-gap-dialog";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "clutch.locationNudgeDismissedUntil";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

interface LocationNudgeBannerProps {
	referenceData: OnboardingReferenceData;
	defaultState?: string;
	defaultDistrict?: string;
	message?: string;
}

export function LocationNudgeBanner({
	referenceData,
	defaultState,
	defaultDistrict,
	message = "Set your district to see tournaments and venues near you.",
}: LocationNudgeBannerProps) {
	const router = useRouter();
	const [hydrated, setHydrated] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);

	useEffect(() => {
		setHydrated(true);
		try {
			const raw = window.localStorage.getItem(DISMISS_KEY);
			if (!raw) return;
			const until = Number.parseInt(raw, 10);
			if (Number.isFinite(until) && until > Date.now()) {
				setDismissed(true);
			}
		} catch {
			// ignore storage errors
		}
	}, []);

	function handleDismiss() {
		setDismissed(true);
		try {
			window.localStorage.setItem(
				DISMISS_KEY,
				String(Date.now() + DISMISS_TTL_MS),
			);
		} catch {
			// ignore
		}
	}

	if (!hydrated || dismissed) {
		return null;
	}

	return (
		<>
			<div className="mb-6 flex items-center gap-3 rounded-2xl border border-border/50 bg-muted/30 px-4 py-3 sm:px-5 dark:border-0 dark:bg-muted/20">
				<div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background/70">
					<HugeiconsIcon icon={Location01Icon} size={22} />
				</div>
				<p className="flex-1 text-pretty text-base text-muted-foreground leading-6">
					{message}
				</p>
				<div className="flex shrink-0 items-center gap-2">
					<Button type="button" size="lg" onClick={() => setDialogOpen(true)}>
						Set location
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						aria-label="Dismiss"
						onClick={handleDismiss}
					>
						<HugeiconsIcon icon={Cancel01Icon} size={16} />
					</Button>
				</div>
			</div>

			<ProfileGapDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				needsLocation
				referenceData={referenceData}
				defaultState={defaultState}
				defaultDistrict={defaultDistrict}
				title="Set your location"
				description="We'll use this to surface tournaments and venues near you."
				onComplete={() => {
					router.refresh();
				}}
			/>
		</>
	);
}
